import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  GridLegacy as Grid,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  List,
  ListItem,
  ListItemText,
  Tooltip,
} from '@mui/material';
import { AppButton, AppIconButton, AppTextField } from '../../components/design-system';
import {
  ArrowBack as BackIcon,
  Refresh as RefreshIcon,
  LinkOff as UnassignIcon,
  PowerOff as DecommissionIcon,
  Block as RetireIcon,
  AssignmentReturn as ReturnIcon,
  Edit as EditIcon,
  SettingsBackupRestore as RecoveryIcon,
  Replay as ResetDeviceIcon,
  RestartAlt as RebootIcon,
  LockOpen as AllowIcon,
  VpnKey as SsmIcon,
  Key as BreakGlassIcon,
  ContentCopy as CopyIcon,
} from '@mui/icons-material';
import { opsDevicesService } from '../../services/OpsDevicesService';
import type { OpsDeviceDetail, OpsIotProvisioningStatus, OpsDeviceStateHistoryEntry } from '@cnapse/shared/schemas.js';
import { isNonIotDeviceCategory, PRODUCT_NAMES } from '@cnapse/shared';
import UnassignModal from './components/UnassignModal';
import DecommissionModal from './components/DecommissionModal';
import ResetDeviceModal from './components/ResetDeviceModal';
import RetireDeviceModal from './components/RetireDeviceModal';
import ReturnDeviceModal from './components/ReturnDeviceModal';
import DeviceFormDialog from './components/DeviceFormDialog';
import { EfemInstallationCard } from '../../components/efem/commissioning/EfemInstallationCard';
import { GatewayAdapterAssignmentPanel } from '../../components/efem/GatewayAdapterAssignmentPanel';

const STATUS_COLORS: Record<OpsIotProvisioningStatus, 'default' | 'info' | 'success' | 'error'> = {
  not_provisioned: 'error',
  provisioned: 'success',
};

const IOT_PROVISIONING_STATUS_LABELS: Record<OpsIotProvisioningStatus, string> = {
  provisioned: 'Provisioned',
  not_provisioned: 'Not Provisioned',
};

const NON_IOT_STATUS_COLORS: Record<string, 'default' | 'info' | 'warning' | 'success' | 'error'> = {
  'in-stock': 'default', allocated: 'warning', shipped: 'info',
  claimed: 'info', active: 'success', returned: 'warning', retired: 'error',
};
const NON_IOT_STATUS_LABELS: Record<string, string> = {
  'in-stock': 'In Stock', allocated: 'Allocated', shipped: 'Shipped',
  claimed: 'Claimed', active: 'Active', returned: 'Returned', retired: 'Retired',
};

function formatStateLabel(state: string): string {
  return NON_IOT_STATUS_LABELS[state]
    ?? IOT_PROVISIONING_STATUS_LABELS[state as OpsIotProvisioningStatus]
    ?? (state === 'created' ? 'Created' : state);
}

function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <Box display="flex" alignItems="baseline" gap={1} py={0.75}>
      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 180, flexShrink: 0 }}>
        {label}
      </Typography>
      <Typography variant="body2" fontFamily="monospace" component="div">
        {value ?? '—'}
      </Typography>
    </Box>
  );
}

function HistoryEntry({ entry }: { entry: OpsDeviceStateHistoryEntry }) {
  return (
    <ListItem alignItems="flex-start" sx={{ px: 0 }}>
      <ListItemText
        primaryTypographyProps={{ component: 'div' }}
        secondaryTypographyProps={{ component: 'div' }}
        primary={
          <Box display="flex" alignItems="center" gap={1}>
            <Chip label={formatStateLabel(entry.from)} size="small" variant="outlined" />
            <Typography variant="body2" color="text.secondary">→</Typography>
            <Chip label={formatStateLabel(entry.to)} size="small" />
          </Box>
        }
        secondary={
          <Box mt={0.5}>
            <Typography variant="caption" display="block">
              {new Date(entry.timestamp).toLocaleString()} · {entry.actor}
            </Typography>
            {entry.reason && (
              <Typography variant="caption" color="text.secondary" display="block">
                Reason: {entry.reason}
              </Typography>
            )}
          </Box>
        }
      />
    </ListItem>
  );
}

function extractSerialFromPath() {
  const parts = window.location.pathname.split('/');
  return parts[parts.length - 1] ?? '';
}

export default function DeviceDetail() {
  const serialNumber = extractSerialFromPath();

  const [device, setDevice] = useState<OpsDeviceDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState('');
  const [gateways, setGateways] = useState<Array<{ serialNumber: string; label: string }>>([]);
  const [actionLoading, setActionLoading] = useState(false);

  // Modals
  const [decommissionOpen, setDecommissionOpen] = useState(false);
  const [unassignOpen, setUnassignOpen] = useState(false);
  const [retireOpen, setRetireOpen] = useState(false);
  const [returnOpen, setReturnOpen] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);

  const load = useCallback(async () => {
    if (!serialNumber) return;
    setLoading(true);
    setError(null);
    try {
      const [res, gwList] = await Promise.all([
        opsDevicesService.getDevice(serialNumber),
        opsDevicesService.listDevices({ deviceCategory: 'efem-gateway', limit: 200 }),
      ]);
      setDevice(res.data.device);
      setGateways(
        gwList.data.devices.map((g) => ({
          serialNumber: g.serialNumber,
          label: `${g.serialNumber}${g.tenantId ? '' : ' (unassigned)'}`,
        })),
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load device');
    } finally {
      setLoading(false);
    }
  }, [serialNumber]);

  useEffect(() => {
    void load();
  }, [load]);

  const goBack = () => {
    window.history.pushState({}, '', '/provider/devices/inventory');
    window.dispatchEvent(new PopStateEvent('popstate'));
  };

  const [editOpen, setEditOpen] = useState(false);
  const [breakGlassOpen, setBreakGlassOpen] = useState(false);
  const [breakGlassPassword, setBreakGlassPassword] = useState('');
  const [breakGlassPasswordVisible, setBreakGlassPasswordVisible] = useState(false);
  const iotProvisioningStatus: OpsIotProvisioningStatus = device?.iotProvisioningStatus ?? 'not_provisioned';
  const isIotGateway = Boolean(device && !isNonIotDeviceCategory(device.deviceCategory));
  const awaitingReprovision =
    isIotGateway &&
    iotProvisioningStatus === 'not_provisioned' &&
    device?.inventoryStatus === 'claimed' &&
    Boolean(device.certificateRevokedAt || device.deactivatedAt);
  const hasSsmTarget = Boolean(device?.ssmInstanceId);
  const ssmReachableWhileDeprovisioned =
    hasSsmTarget &&
    isIotGateway &&
    iotProvisioningStatus === 'not_provisioned' &&
    Boolean(device?.certificateRevokedAt || device?.deactivatedAt);
  const inventoryStatus = device?.inventoryStatus ?? 'in-stock';
  const isAssigned = Boolean(device?.tenantId);
  const canUnassign =
    isIotGateway &&
    isAssigned &&
    inventoryStatus === 'allocated' &&
    iotProvisioningStatus === 'not_provisioned';
  const canReturn =
    inventoryStatus === 'shipped' || inventoryStatus === 'claimed' || inventoryStatus === 'active';
  const canResetForRedeployment =
    inventoryStatus === 'returned' ||
    (isIotGateway &&
      isAssigned &&
      inventoryStatus !== 'allocated' &&
      inventoryStatus !== 'retired' &&
      iotProvisioningStatus === 'not_provisioned');
  const canRetire =
    inventoryStatus !== 'retired' &&
    (inventoryStatus === 'active'
      || inventoryStatus === 'returned'
      || (inventoryStatus === 'in-stock' && !isAssigned));
  const isTerminalInventoryStatus = inventoryStatus === 'retired' || inventoryStatus === 'returned';
  const canManageProvisionedDevice =
    iotProvisioningStatus === 'provisioned' && !isTerminalInventoryStatus;

  const handleUnassignSuccess = async () => {
    setSuccessMsg('Device unallocated.');
    await load();
  };

  const handleAllowReprovisioning = async () => {
    const reason = window.prompt('Reason for authorizing re-provisioning?');
    if (!reason) return;

    setActionLoading(true);
    try {
      await opsDevicesService.allowDeviceReprovisioning(serialNumber, reason);
      setSuccessMsg('Re-provisioning authorized.');
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Action failed');
    } finally {
      setActionLoading(false);
    }
  };

  const handleTriggerRecovery = async () => {
    if (!window.confirm('WARNING: This will remotely wipe the existing Greengrass config and trigger a full re-provisioning cycle. Continue?')) {
      return;
    }
    const reason = window.prompt('Reason for remote recovery trigger?');
    if (!reason) return;

    setActionLoading(true);
    try {
      await opsDevicesService.triggerDeviceReprovisioning(serialNumber, reason);
      setSuccessMsg('Remote recovery command sent successfully.');
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Action failed');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRevealBreakGlassPassword = async () => {
    if (
      !window.confirm(
        'Reveal the per-device CnapseAdmin break-glass password? This action is audit-logged.',
      )
    ) {
      return;
    }
    const reason = window.prompt('Reason for revealing break-glass password?');
    if (!reason || reason.trim().length < 3) return;

    setActionLoading(true);
    try {
      const response = await opsDevicesService.revealBreakGlassPassword(serialNumber, reason.trim());
      setBreakGlassPassword(response.data.password);
      setBreakGlassOpen(true);
      setSuccessMsg('Break-glass password revealed (audit logged).');
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Action failed');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCopyBreakGlassPassword = async () => {
    try {
      await navigator.clipboard.writeText(breakGlassPassword);
      setSuccessMsg('Password copied to clipboard.');
    } catch {
      setError('Failed to copy password to clipboard.');
    }
  };

  const handleReboot = async () => {
    if (!window.confirm('Are you sure you want to remotely reboot this device?')) {
      return;
    }
    const reason = window.prompt('Reason for remote reboot? (required for audit log)');
    if (!reason?.trim()) return;

    setActionLoading(true);
    try {
      await opsDevicesService.rebootDevice(serialNumber, reason.trim());
      setSuccessMsg('Reboot command sent successfully.');
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Action failed');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDecommissionSuccess = () => {
    setSuccessMsg('Device decommissioned.');
    setDecommissionOpen(false);
    void load();
  };

  const handleRetireSuccess = () => {
    setSuccessMsg('Device retired.');
    setRetireOpen(false);
    void load();
  };

  const handleReturnSuccess = () => {
    setSuccessMsg('Device marked as returned.');
    setReturnOpen(false);
    void load();
  };

  const handleResetSuccess = () => {
    setSuccessMsg('Device reset to manufactured.');
    setResetOpen(false);
    void load();
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !device) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      {/* Header */}
      <Box display="flex" alignItems="center" gap={1} mb={3}>
        <Tooltip title="Back to inventory">
          <AppIconButton onClick={goBack} size="small">
            <BackIcon />
          </AppIconButton>
        </Tooltip>
        <Typography variant="h5" fontWeight="bold" fontFamily="monospace">
          {serialNumber}
        </Typography>
        {device && (() => {
          const label = formatStateLabel(inventoryStatus);
          const color = NON_IOT_STATUS_COLORS[inventoryStatus] ?? 'default';
          return (
            <>
              <Chip label={label} color={color} size="small" sx={{ ml: 1 }} />
              {isIotGateway && (
                <Chip
                  label={IOT_PROVISIONING_STATUS_LABELS[iotProvisioningStatus]}
                  color={STATUS_COLORS[iotProvisioningStatus]}
                  size="small"
                  variant="outlined"
                />
              )}
            </>
          );
        })()}
        <Tooltip title="Edit device details">
          <span>
            <AppIconButton onClick={() => setEditOpen(true)} disabled={!device} size="small" sx={{ ml: 0.5 }}>
              <EditIcon fontSize="small" />
            </AppIconButton>
          </span>
        </Tooltip>
        <Box flexGrow={1} />

        {/* Lifecycle actions (all device categories) */}
        {device && (canReturn || canRetire || canResetForRedeployment) && (
          <Box display="flex" alignItems="center" gap={1} mr={1}>
            {canReturn && (
              <AppButton
                variant="outlined"
                color="warning"
                size="small"
                startIcon={<ReturnIcon />}
                onClick={() => setReturnOpen(true)}
                disabled={actionLoading}
              >
                Return
              </AppButton>
            )}

            {canResetForRedeployment && (
              <AppButton
                variant="outlined"
                color="warning"
                size="small"
                startIcon={<ResetDeviceIcon />}
                onClick={() => setResetOpen(true)}
                disabled={actionLoading}
              >
                Reset for Redeployment
              </AppButton>
            )}

            {canRetire && (
              <AppButton
                variant="outlined"
                color="error"
                size="small"
                startIcon={<RetireIcon />}
                onClick={() => setRetireOpen(true)}
                disabled={actionLoading}
              >
                Retire
              </AppButton>
            )}
          </Box>
        )}

        {/* IoT gateway operations */}
        {device && isIotGateway && (
          <Box display="flex" alignItems="center" gap={1} mr={1}>
            {canUnassign && (
              <AppButton
                variant="outlined"
                color="inherit"
                size="small"
                startIcon={actionLoading ? <CircularProgress size={16} /> : <UnassignIcon />}
                onClick={() => setUnassignOpen(true)}
                disabled={actionLoading}
              >
                Unassign
              </AppButton>
            )}

            {canManageProvisionedDevice && (
              <>
                <AppButton
                  variant="outlined"
                  color="warning"
                  size="small"
                  startIcon={actionLoading ? <CircularProgress size={16} /> : <BreakGlassIcon />}
                  onClick={handleRevealBreakGlassPassword}
                  disabled={actionLoading}
                >
                  Break-Glass Password
                </AppButton>
                <AppButton
                  variant="outlined"
                  color="error"
                  size="small"
                  startIcon={<DecommissionIcon />}
                  onClick={() => setDecommissionOpen(true)}
                >
                  Decommission
                </AppButton>
              </>
            )}

            {awaitingReprovision && !device.reprovisioningPermitted && (
              <AppButton
                variant="outlined"
                color="primary"
                size="small"
                startIcon={actionLoading ? <CircularProgress size={16} /> : <AllowIcon />}
                onClick={handleAllowReprovisioning}
                disabled={actionLoading}
              >
                Unlock Reprovisioning
              </AppButton>
            )}

            {awaitingReprovision && device.reprovisioningPermitted && (
              <Tooltip title={hasSsmTarget ? '' : 'SSM instance ID missing — re-run provisioning on the device or backfill inventory'}>
                <span>
                  <AppButton
                    variant="contained"
                    color="primary"
                    size="small"
                    startIcon={actionLoading ? <CircularProgress size={16} /> : <RecoveryIcon />}
                    onClick={handleTriggerRecovery}
                    disabled={actionLoading || !hasSsmTarget}
                  >
                    Trigger Reprovisioning
                  </AppButton>
                </span>
              </Tooltip>
            )}

            {hasSsmTarget && !isTerminalInventoryStatus && (
              <AppButton
                variant="outlined"
                color="warning"
                size="small"
                startIcon={actionLoading ? <CircularProgress size={16} /> : <RebootIcon />}
                onClick={handleReboot}
                disabled={actionLoading}
              >
                Reboot
              </AppButton>
            )}
          </Box>
        )}

        <Tooltip title="Refresh">
          <AppIconButton onClick={load} disabled={loading}>
            <RefreshIcon />
          </AppIconButton>
        </Tooltip>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {successMsg && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMsg('')}>
          {successMsg}
        </Alert>
      )}

      {device && inventoryStatus === 'returned' && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2" fontWeight="bold" gutterBottom>
            QA disposition required
          </Typography>
          <Typography variant="body2">
            This device was returned to the warehouse. After inspection, restock it with{' '}
            <strong>Reset for Redeployment</strong> (moves to in-stock for reallocation), then
            register the post-reimage AK certificate via <strong>Edit Device</strong> before
            allocating to an order. Or <strong>Retire</strong> if it should be permanently removed
            from service.
          </Typography>
        </Alert>
      )}

      {device && (
        <Grid container spacing={3}>
          {/* Main Details */}
          <Grid item xs={12}>
            {device && (
              <EfemInstallationCard
                device={device}
                portal="provider"
                gateways={gateways}
                onUpdated={() => void load()}
              />
            )}

            {device.deviceCategory === 'efem-gateway' && device.tenantId && (
              <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Alert severity="info" sx={{ mb: 1.5 }}>
                  {PRODUCT_NAMES.AXON} adapter assignment and configuration for this gateway are managed in Axon Setup.
                </Alert>
                <AppButton
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    const url = `/provider/efem-setup?tenantId=${encodeURIComponent(device.tenantId ?? '')}&gateway=${encodeURIComponent(device.serialNumber)}&tab=0`;
                    window.history.pushState({}, '', url);
                    window.dispatchEvent(new PopStateEvent('popstate'));
                  }}
                >
                  Open in Axon Setup →
                </AppButton>
              </Paper>
            )}

            {device.deviceCategory === 'location-gateway' && (
              <GatewayAdapterAssignmentPanel
                kind="location"
                serialNumber={device.serialNumber}
                assignment={device.locationAdapterAssignment}
                deviceProvisioning={{
                  iotProvisioningStatus: device.iotProvisioningStatus,
                  thingName: device.thingName,
                }}
                onAssigned={() => void load()}
              />
            )}

            {/* Identity */}
            <Paper variant="outlined" sx={{ p: 3, mb: 2 }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Device Identity
              </Typography>
              <Divider sx={{ mb: 1.5 }} />
              <DetailRow label="Cnapse Serial Number" value={device.serialNumber} />
              <DetailRow label="Vendor Serial Number" value={device.vendorSerialNumber} />
              <DetailRow label="Model" value={device.deviceModel} />
              <DetailRow label="Mfg Batch" value={device.mfgBatch} />
              <DetailRow label="Mfg Date" value={device.mfgDate} />
              <DetailRow label="EK Hash" value={device.ekHash} />
              <DetailRow
                label="Created"
                value={new Date(device.createdAt).toLocaleString()}
              />
            </Paper>

            {/* Assignment */}
            {(device.tenantId) && (
              <Paper variant="outlined" sx={{ p: 3, mb: 2 }}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Assignment
                </Typography>
                <Divider sx={{ mb: 1.5 }} />
                <DetailRow label="Tenant" value={device.resolvedTenantName ?? device.tenantId} />
                <DetailRow label="Device Category" value={device.deviceCategory} />
                <DetailRow label="Order ID" value={device.orderId} />
                <DetailRow
                  label="Assigned At"
                  value={device.allocatedAt ? new Date(device.allocatedAt).toLocaleString() : null}
                />
              </Paper>
            )}

            {/* AWS IoT */}
            {(device.thingName || device.certificateId) && (
              <Paper variant="outlined" sx={{ p: 3, mb: 2 }}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  AWS IoT
                </Typography>
                <Divider sx={{ mb: 1.5 }} />
                <DetailRow label="Thing Name" value={device.thingName} />
                <DetailRow label="Certificate ID" value={device.certificateId} />
                <DetailRow
                  label="Provisioned At"
                  value={device.activatedAt ? new Date(device.activatedAt).toLocaleString() : null}
                />
                <DetailRow
                  label="Decommissioned At"
                  value={device.deactivatedAt ? new Date(device.deactivatedAt).toLocaleString() : null}
                />
              </Paper>
            )}

            {/* Remote Management (SSM) */}
            {isIotGateway && (hasSsmTarget || awaitingReprovision) && (
              <Paper variant="outlined" sx={{ p: 3, mb: 2 }}>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <SsmIcon color="primary" fontSize="small" />
                  <Typography variant="subtitle1" fontWeight="bold">
                    Remote Management
                  </Typography>
                </Box>
                <Divider sx={{ mb: 1.5 }} />
                {ssmReachableWhileDeprovisioned && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    IoT deprovisioned — SSM agent registration retained for remote recovery commands.
                  </Typography>
                )}
                <DetailRow
                  label="SSM Instance ID"
                  value={
                    device.ssmInstanceId ??
                    'Not linked — appears after first successful provision with SSM agent'
                  }
                />
                {awaitingReprovision && (
                  <DetailRow
                    label="Re-provisioning Permitted"
                    value={
                      <Chip
                        label={device.reprovisioningPermitted ? 'Permitted' : 'Locked'}
                        color={device.reprovisioningPermitted ? 'success' : 'default'}
                        size="small"
                      />
                    }
                  />
                )}
              </Paper>
            )}

            {/* State History */}
            <Paper variant="outlined" sx={{ p: 3 }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                State History
              </Typography>
              <Divider sx={{ mb: 1 }} />
              <Box
                sx={{
                  mb: 2,
                  p: 1.5,
                  borderRadius: 1,
                  bgcolor: 'action.hover',
                }}
              >
                <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                  Current state
                </Typography>
                <Box display="flex" gap={1} flexWrap="wrap">
                  <Chip label={formatStateLabel(inventoryStatus)} size="small" />
                  {isIotGateway && (
                    <Chip
                      label={IOT_PROVISIONING_STATUS_LABELS[iotProvisioningStatus]}
                      size="small"
                      variant="outlined"
                    />
                  )}
                </Box>
              </Box>
              {!device.stateHistory || device.stateHistory.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No state transitions recorded.
                </Typography>
              ) : (
                <List dense disablePadding>
                  {[...device.stateHistory].reverse().map((entry, i) => (
                    <Box key={i}>
                      <HistoryEntry entry={entry} />
                      {i < device.stateHistory!.length - 1 && <Divider />}
                    </Box>
                  ))}
                </List>
              )}
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Modals */}
      <DecommissionModal
        open={decommissionOpen}
        serialNumber={serialNumber}
        onClose={() => setDecommissionOpen(false)}
        onSuccess={handleDecommissionSuccess}
      />

      <UnassignModal
        open={unassignOpen}
        serialNumber={serialNumber}
        onClose={() => setUnassignOpen(false)}
        onSuccess={handleUnassignSuccess}
      />

      <RetireDeviceModal
        open={retireOpen}
        serialNumber={serialNumber}
        onClose={() => setRetireOpen(false)}
        onSuccess={handleRetireSuccess}
      />

      <ReturnDeviceModal
        open={returnOpen}
        serialNumber={serialNumber}
        inventoryStatus={inventoryStatus}
        onClose={() => setReturnOpen(false)}
        onSuccess={handleReturnSuccess}
      />

      <ResetDeviceModal
        open={resetOpen}
        serialNumber={serialNumber}
        onClose={() => setResetOpen(false)}
        onSuccess={handleResetSuccess}
      />
      <DeviceFormDialog
        open={editOpen}
        mode="edit"
        device={device}
        onClose={() => setEditOpen(false)}
        onSuccess={(msg) => { setSuccessMsg(msg); void load(); }}
      />

      <Dialog
        open={breakGlassOpen}
        onClose={() => {
          setBreakGlassOpen(false);
          setBreakGlassPassword('');
          setBreakGlassPasswordVisible(false);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>CnapseAdmin break-glass password</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Handle securely. Do not share in chat or email. Password rotation requires re-provisioning.
          </Alert>
          <AppTextField
            label="Password"
            value={breakGlassPassword}
            fullWidth
            InputProps={{ readOnly: true }}
            type={breakGlassPasswordVisible ? 'text' : 'password'}
            onFocus={() => setBreakGlassPasswordVisible(true)}
            onBlur={() => setBreakGlassPasswordVisible(false)}
          />
        </DialogContent>
        <DialogActions>
          <AppButton onClick={handleCopyBreakGlassPassword} startIcon={<CopyIcon />}>
            Copy
          </AppButton>
          <AppButton
            variant="contained"
            onClick={() => {
              setBreakGlassOpen(false);
              setBreakGlassPassword('');
              setBreakGlassPasswordVisible(false);
            }}
          >
            Close
          </AppButton>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
